import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { FeatherZoomIn } from "@subframe/core";
import { FeatherZoomOut } from "@subframe/core";
import { FeatherDownload } from "@subframe/core";
import { FeatherRefreshCw } from "@subframe/core";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { getSignedUrlForDocument } from "../services/api";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
const isImageFile = (filename: string): boolean =>
  IMAGE_EXTENSIONS.some((ext) =>
    filename.toLowerCase().endsWith(ext.toLowerCase())
  );

interface DocumentViewerProps {
  pdfUrl?: string; // Made optional since we'll fetch it
  documentId?: number; // Document ID for fetching signed URL
  documentType?: "bill" | "invoice" | "debit_note" | "credit_note"; // Document type for fetching signed URL
  fileName?: string;
  className?: string;
  onError?: (error: string) => void;
  onUrlRefresh?: (newUrl: string, filename: string) => void; // Callback when URL is refreshed
  /**
   * Render every page stacked vertically, scaled to fit the pane width,
   * so scrolling flows from page to page (no Previous/Next buttons).
   * Zoom still applies on top of the fitted width.
   */
  continuousScroll?: boolean;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  pdfUrl: initialPdfUrl,
  documentId,
  documentType,
  fileName: initialFileName = "document.pdf",
  className = "",
  onError,
  onUrlRefresh,
  continuousScroll = false,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [documentLoading, setDocumentLoading] = useState<boolean>(true);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxRetries = 3;
  // Monotonic id per fetch chain. A remount or manual retry bumps it, so a
  // stale chain (e.g. React StrictMode's dev double-mount) can never set
  // state or leave a blob URL that the live <Document> is reading get
  // revoked underneath it.
  const fetchRunRef = useRef(0);
  const [currentDocumentUrl, setCurrentDocumentUrl] = useState<string | null>(
    initialPdfUrl || null
  );
  const [fileName, setFileName] = useState<string>(initialFileName);
  const [isImage, setIsImage] = useState<boolean>(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  // Width of the scroll pane — pages are fitted to it in continuous mode
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (!continuousScroll) return;
    const el = contentRef.current;
    if (!el) return;

    const measure = () => setContainerWidth(el.clientWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [continuousScroll]);

  // Memoized values to prevent unnecessary re-renders
  const memoizedPdfFile = useMemo(() => pdfData, [pdfData]);

  const memoizedOnDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
    },
    []
  );

  const memoizedOnDocumentLoadError = useCallback((error: Error) => {
    console.error("PDF load error:", error);
    setDocumentError("Failed to load PDF document");
  }, []);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setDocumentError("Failed to load image");
    setDocumentLoading(false);
    onError?.("Failed to load image");
  }, [onError]);

  // Fetch signed URL from backend
  const fetchSignedUrl = useCallback(async (): Promise<{
    url: string;
    filename: string;
  } | null> => {
    if (!documentId || !documentType) {
      return initialPdfUrl
        ? { url: initialPdfUrl, filename: initialFileName }
        : null;
    }

    try {
      const response = await getSignedUrlForDocument({
        documentId,
        documentType,
        expiration: 3600, // 1 hour
      });

      if (response.success && response.data.signedUrl) {
        const newUrl = response.data.signedUrl;
        const newFileName = response.data.filename || initialFileName;

        setCurrentDocumentUrl(newUrl);
        setFileName(newFileName);

        // Call callback to update parent component
        if (onUrlRefresh) {
          onUrlRefresh(newUrl, newFileName);
        }

        return { url: newUrl, filename: newFileName };
      } else {
        throw new Error("Failed to get signed URL from response");
      }
    } catch (error) {
      console.error("Error fetching signed URL:", error);
      const errorMsg =
        error instanceof Error ? error.message : "Failed to fetch signed URL";
      setDocumentError(errorMsg);
      onError?.(errorMsg);
      return null;
    }
  }, [
    documentId,
    documentType,
    initialPdfUrl,
    initialFileName,
    onUrlRefresh,
    onError,
  ]);

  // Fetch PDF as blob with retry logic
  const fetchPdf = useCallback(
    async (attempt: number, urlToFetch?: string, runId?: number) => {
      const isStale = () =>
        runId !== undefined && runId !== fetchRunRef.current;

      try {
        setDocumentLoading(true);
        setDocumentError(null);

        const urlToUse = urlToFetch || currentDocumentUrl;

        if (!urlToUse) {
          throw new Error("No document URL available");
        }

        const response = await fetch(urlToUse, {
          mode: "cors",
        });

        if (response.ok) {
          const blob = await response.blob();
          if (isStale()) {
            return;
          }
          const url = URL.createObjectURL(blob);
          setPdfData(url);
          setRetryCount(0);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        if (isStale()) {
          return;
        }
        console.error(`PDF fetch error (attempt ${attempt + 1}):`, error);

        if (attempt < maxRetries) {
          const nextAttempt = attempt + 1;
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000);

          setRetryCount(nextAttempt);

          retryTimeoutRef.current = setTimeout(() => {
            fetchPdf(nextAttempt, urlToFetch, runId);
          }, delay);
        } else {
          const errorMsg = "Failed to load document after multiple attempts";
          console.error(errorMsg);
          setDocumentError(errorMsg);
          onError?.(errorMsg);
        }
      } finally {
        if (!isStale()) {
          setDocumentLoading(false);
        }
      }
    },
    [currentDocumentUrl, onError, maxRetries]
  );

  // Initial fetch on mount or URL change
  useEffect(() => {
    const runId = ++fetchRunRef.current;

    const initializeDocument = async () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      setPdfData(null);
      setDocumentError(null);
      setRetryCount(0);
      setImageLoaded(false);

      const result = await fetchSignedUrl();
      if (runId !== fetchRunRef.current) {
        return;
      }

      if (result) {
        const { url, filename } = result;
        setCurrentDocumentUrl(url);
        setFileName(filename);

        const fileIsImage = isImageFile(filename);
        setIsImage(fileIsImage);

        if (fileIsImage) {
          // Image loads via img onLoad - no blob fetch needed
          setDocumentLoading(false);
        } else {
          fetchPdf(0, url, runId);
        }
      } else if (currentDocumentUrl) {
        // Use initial URL without documentId (e.g. passed from parent)
        const fileIsImage = isImageFile(initialFileName);
        setIsImage(fileIsImage);

        if (fileIsImage) {
          setDocumentLoading(false);
        } else {
          fetchPdf(0, currentDocumentUrl, runId);
        }
      } else {
        setDocumentError("No document URL or document information provided");
        setDocumentLoading(false);
      }
    };

    initializeDocument();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, documentType]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfData) {
        URL.revokeObjectURL(pdfData);
      }
    };
  }, [pdfData]);

  const pdfOptions = useMemo(
    () => ({
      cMapUrl: "https://unpkg.com/pdfjs-dist@3.11.174/cmaps/",
      cMapPacked: true,
      standardFontDataUrl:
        "https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/",
    }),
    []
  );

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.2, 3.0));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  }, []);

  const handleDownload = useCallback(() => {
    if (pdfData) {
      const link = document.createElement("a");
      link.href = pdfData;
      link.download = fileName;
      link.click();
    } else if (currentDocumentUrl) {
      const link = document.createElement("a");
      link.href = currentDocumentUrl;
      link.download = fileName;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    }
  }, [pdfData, fileName, currentDocumentUrl]);

  const handlePreviousPage = useCallback(() => {
    if (pageNumber <= 1) return;
    setPageNumber((prev) => Math.max(prev - 1, 1));
  }, [pageNumber]);

  const handleNextPage = useCallback(() => {
    if (pageNumber >= numPages) return;
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  }, [pageNumber, numPages]);

  // Manual retry handler
  const handleManualRetry = useCallback(async () => {
    const runId = ++fetchRunRef.current;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    setPdfData(null);
    setDocumentError(null);
    setRetryCount(0);
    setImageLoaded(false);

    if (documentId && documentType) {
      const result = await fetchSignedUrl();
      if (runId !== fetchRunRef.current) {
        return;
      }
      if (result) {
        const { url, filename } = result;
        const fileIsImage = isImageFile(filename);
        setIsImage(fileIsImage);

        if (fileIsImage) {
          setDocumentLoading(false);
        } else {
          fetchPdf(0, url, runId);
        }
      }
    } else {
      fetchPdf(0, undefined, runId);
    }
  }, [documentId, documentType, fetchSignedUrl, fetchPdf]);

  const loadingMessage =
    retryCount > 0
      ? `Retrying... (Attempt ${retryCount + 1}/${maxRetries + 1})`
      : "Loading document...";

  return (
    <div
      className={`flex flex-col items-center self-stretch overflow-hidden rounded-md border border-solid border-neutral-border bg-default-background shadow-sm ${className}`}
    >
      {/* Header with Page Navigation */}
      <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border bg-neutral-50 px-4 py-3">
        <div className="flex items-center gap-4">
          <span className="text-body-bold font-body-bold text-default-font">
            {fileName}
          </span>
          {!isImage && numPages > 1 && continuousScroll && (
            <span className="text-caption font-caption text-subtext-color">
              {numPages} pages
            </span>
          )}
          {!isImage && numPages > 1 && !continuousScroll && (
            <div className="flex items-center gap-2">
              <Button
                variant="neutral-tertiary"
                size="small"
                onClick={handlePreviousPage}
                disabled={pageNumber <= 1}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-caption font-caption text-subtext-color">
                  Page
                </span>
                <input
                  type="number"
                  min="1"
                  max={numPages}
                  value={pageNumber}
                  readOnly={true}
                  className="w-12 px-2 py-1 text-center text-caption font-caption border border-neutral-border rounded"
                />
                <span className="text-caption font-caption text-subtext-color">
                  of {numPages}
                </span>
              </div>
              <Button
                variant="neutral-tertiary"
                size="small"
                onClick={handleNextPage}
                disabled={pageNumber >= numPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            icon={<FeatherZoomOut />}
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
          />
          <span className="text-caption font-caption text-subtext-color">
            {Math.round(scale * 100)}%
          </span>
          <IconButton
            icon={<FeatherZoomIn />}
            onClick={handleZoomIn}
            disabled={scale >= 3.0}
          />
          <IconButton icon={<FeatherDownload />} onClick={handleDownload} />
        </div>
      </div>

      {/* Document Content */}
      <div
        ref={contentRef}
        className={`flex w-full grow shrink-0 basis-0 bg-neutral-100 overflow-auto ${
          continuousScroll ? "flex-col items-center" : "items-center justify-center"
        }`}
      >
        {documentLoading ? (
          <div className="flex flex-col items-center justify-center p-8 gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            <span className="text-body font-body text-subtext-color">
              {retryCount > 0
                ? `Waiting to retry... (Attempt ${retryCount + 1}/${maxRetries + 1})`
                : loadingMessage}
            </span>
          </div>
        ) : retryCount > 0 && !documentError && !pdfData && !isImage ? (
          <div className="flex flex-col items-center justify-center p-8 gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            <div className="flex flex-col items-center gap-2">
              <span className="text-body font-body text-subtext-color">
                Waiting to retry... (Attempt {retryCount + 1}/{maxRetries + 1})
              </span>
              <span className="text-caption font-caption text-subtext-color">
                The document will load automatically
              </span>
            </div>
          </div>
        ) : documentError ? (
          <div className="flex flex-col items-center justify-center p-8 gap-4">
            <div className="flex flex-col items-center gap-2 text-center">
              <span className="text-body-bold font-body-bold text-error-600">
                {documentError}
              </span>
              <span className="text-caption font-caption text-subtext-color">
                The document failed to load after {maxRetries + 1} attempts
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="brand-primary"
                size="small"
                icon={<FeatherRefreshCw />}
                onClick={handleManualRetry}
              >
                Try Again
              </Button>
              <Button
                variant="neutral-secondary"
                size="small"
                onClick={() =>
                  currentDocumentUrl && window.open(currentDocumentUrl, "_blank")
                }
                disabled={!currentDocumentUrl}
              >
                Open in New Tab
              </Button>
            </div>
          </div>
        ) : isImage && currentDocumentUrl ? (
          <div className="relative flex flex-col items-center justify-center p-4 min-h-[400px] w-full">
            {!imageLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-neutral-100">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                <span className="text-caption font-caption text-subtext-color">
                  Loading image...
                </span>
              </div>
            )}
            <img
              src={currentDocumentUrl}
              alt={fileName}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                transform: `scale(${scale})`,
                transformOrigin: "center center",
                maxWidth: "100%",
                objectFit: "contain",
                opacity: imageLoaded ? 1 : 0,
              }}
              className="transition-opacity duration-200"
            />
          </div>
        ) : pdfData ? (
          <div className="flex flex-col items-center gap-4 p-4">
            <Document
              file={memoizedPdfFile}
              options={pdfOptions}
              onLoadSuccess={memoizedOnDocumentLoadSuccess}
              onLoadError={memoizedOnDocumentLoadError}
              loading={
                <div className="flex items-center justify-center p-8">
                  <span className="text-body font-body text-subtext-color">
                    Loading PDF...
                  </span>
                </div>
              }
              error={
                <div className="flex items-center justify-center p-8">
                  <span className="text-body font-body text-error-600">
                    Failed to load PDF
                  </span>
                </div>
              }
            >
              {continuousScroll && numPages > 0 ? (
                Array.from({ length: numPages }, (_, index) => (
                  <div key={`page-wrap-${index + 1}`} className="mb-4 shadow-sm">
                    <Page
                      pageNumber={index + 1}
                      width={Math.max((containerWidth - 32) * scale, 200)}
                      renderTextLayer={false}
                      renderAnnotationLayer={true}
                      loading={
                        <div className="flex items-center justify-center p-4">
                          <span className="text-caption font-caption text-subtext-color">
                            Loading page {index + 1}...
                          </span>
                        </div>
                      }
                    />
                  </div>
                ))
              ) : (
                <Page
                  key={`page-${pageNumber}-${scale}`}
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={true}
                  loading={
                    <div className="flex items-center justify-center p-4">
                      <span className="text-caption font-caption text-subtext-color">
                        Loading page {pageNumber}...
                      </span>
                    </div>
                  }
                />
              )}
            </Document>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-8">
            <span className="text-body font-body text-error-600">
              No document data available
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
